import { asAreaUnit, type AreaUnit } from '../../utils/units';
import { areaWords, SERVICE_AREA_LIMITS } from '../../utils/serviceUnits';
import { ServiceType } from '../../../__generated__/graphql';
import type { IProviderWorkerFieldsFragment } from '../../services/serviceService/__generated__/queries.generated';

/**
 * The price form of a worker's services (spec 019 D4b, D4c, D8, D26). Pure:
 * the wizard, the manage page and the price page share it. The shapes match
 * the API's `priceTable` JSON (data-model §5); the defaults mirror the
 * backend `seed/default-price-tables.ts`, so a new service starts filled in.
 */

export type Offering = IProviderWorkerFieldsFragment['worker']['offerings'][number];
export type PriceWarning = IProviderWorkerFieldsFragment['priceWarnings'][number];

export const MAX_PRICE = 9999;
export const MAX_HOURLY_RATE = 999;

export const SERVICE_TYPES: readonly ServiceType[] = [
  ServiceType.HomeTurnover,
  ServiceType.VehicleCleaning,
  ServiceType.VehicleHandover,
];

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  [ServiceType.HomeTurnover]: 'Home turnover',
  [ServiceType.VehicleCleaning]: 'Vehicle cleaning',
  [ServiceType.VehicleHandover]: 'Vehicle handover',
};

export const SERVICE_TYPE_DESCRIPTIONS: Record<ServiceType, string> = {
  [ServiceType.HomeTurnover]:
    'Clean a rental home between guests. Priced by bedrooms and bathrooms.',
  [ServiceType.VehicleCleaning]: 'Clean a car inside and out. Priced by vehicle size and level.',
  [ServiceType.VehicleHandover]:
    'Meet a renter and hand over the car. Paid by the hour. Needs a verified background check.',
};

/** The API's own name of each service, used in price warning fields (`home_turnover.basePrice`). */
export const SERVICE_TYPE_KEY: Record<ServiceType, string> = {
  [ServiceType.HomeTurnover]: 'home_turnover',
  [ServiceType.VehicleCleaning]: 'vehicle_cleaning',
  [ServiceType.VehicleHandover]: 'vehicle_handover',
};

export const VEHICLE_CLASSES = ['car', 'suv_truck', 'large', 'luxury'] as const;
export type VehicleClassKey = (typeof VEHICLE_CLASSES)[number];
export const CLEANING_LEVELS = ['quick', 'standard', 'deep'] as const;
export type CleaningLevelKey = (typeof CLEANING_LEVELS)[number];

export const VEHICLE_CLASS_LABELS: Record<VehicleClassKey, string> = {
  car: 'Car',
  suv_truck: 'SUV or truck',
  large: '3-row SUV or van',
  luxury: 'Luxury',
};

export const CLEANING_LEVEL_LABELS: Record<CleaningLevelKey, string> = {
  quick: 'Quick',
  standard: 'Standard',
  deep: 'Deep',
};

/** What each vehicle cleaning level includes (spec 019 D4c, W25). */
export const CLEANING_LEVEL_DESCRIPTIONS: Record<CleaningLevelKey, string> = {
  quick: 'Vacuum, wipe the inside and clean the windows. Made for between two trips.',
  standard: 'Everything in Quick, plus a wash outside.',
  deep: 'Everything in Standard, plus seats shampooed and odors removed.',
};

// ─── defaults (backend `default-price-tables.ts`) ───────────────────────────

export const DEFAULT_HOME_TABLE = Object.freeze({
  basePrice: 60,
  perExtraBedroom: 20,
  perExtraBathroom: 15,
  overrides: [] as { bedrooms: number; bathrooms: number; price: number }[],
  largeHomeSurcharge: null as number | null,
  largeHomeSqft: 2000,
  sameDayTurnoverSurcharge: null as number | null,
});

export const DEFAULT_VEHICLE_TABLE: Readonly<
  Record<VehicleClassKey, Record<CleaningLevelKey, number | null>>
> = Object.freeze({
  car: { quick: 35, standard: 60, deep: 120 },
  suv_truck: { quick: 45, standard: 75, deep: 150 },
  large: { quick: 55, standard: 90, deep: null },
  luxury: { quick: null, standard: 110, deep: 200 },
});

export const DEFAULT_HANDOVER_HOURLY_RATE = 40;

export interface AddOnForm {
  code: string;
  label: string;
  price: string;
  unit: string;
  minutes: number | null;
}

interface AddOnDefault {
  code: string;
  label: string;
  price: number;
  unit: string;
  minutes: number | null;
}

/** Add-ons a new service starts with (O2 drafts on the backend, except the trash bag). */
export const DEFAULT_ADD_ONS: Readonly<Record<ServiceType, readonly AddOnDefault[]>> =
  Object.freeze({
    [ServiceType.HomeTurnover]: [
      {
        code: 'extra_trash_bag',
        label: 'Extra trash bag',
        price: 15,
        unit: 'per_item',
        minutes: 5,
      },
      {
        code: 'laundry_takeaway',
        label: 'Laundry taken away',
        price: 30,
        unit: 'each',
        minutes: 0,
      },
      { code: 'restock', label: 'Restock guest supplies', price: 10, unit: 'each', minutes: 10 },
    ],
    [ServiceType.VehicleCleaning]: [
      { code: 'pet_hair', label: 'Pet hair removal', price: 30, unit: 'each', minutes: 30 },
      { code: 'odor', label: 'Odor treatment', price: 40, unit: 'each', minutes: 30 },
      { code: 'sand', label: 'Sand removal', price: 25, unit: 'each', minutes: 20 },
    ],
    [ServiceType.VehicleHandover]: [],
  });

/** Estimated minutes per item (backend `DEFAULT_DURATION_RULES`), for the price per hour. */
export const DEFAULT_DURATIONS = Object.freeze({
  home: { baseMinutes: 120, perBedroom: 30, perBathroom: 20 },
  vehicle: { quick: 45, standard: 90, deep: 180 } as Record<CleaningLevelKey, number>,
  vehicleFactor: { car: 1, suv_truck: 1.2, large: 1.4, luxury: 1.2 } as Record<
    VehicleClassKey,
    number
  >,
});

// ─── form model ─────────────────────────────────────────────────────────────

export interface HomeOverrideForm {
  bedrooms: string;
  bathrooms: string;
  price: string;
}

export interface HomePriceForm {
  basePrice: string;
  perExtraBedroom: string;
  perExtraBathroom: string;
  overrides: HomeOverrideForm[];
  largeHomeSurcharge: string;
  /** In `largeHomeAreaUnit` (spec 028). */
  largeHomeSqft: string;
  /** Spec 028: a saved table's own unit, else the brand's (square metres when the worker is in km). */
  largeHomeAreaUnit: AreaUnit;
  sameDayTurnoverSurcharge: string;
}

/** An empty cell means the worker does not offer that class and level. */
export type VehiclePriceForm = Record<VehicleClassKey, Record<CleaningLevelKey, string>>;

export interface OfferingForm {
  serviceType: ServiceType;
  /** Offered and bookable; off keeps the saved prices. */
  enabled: boolean;
  /** Set once the API has the offering. */
  offeringId: string | null;
  home: HomePriceForm;
  vehicle: VehiclePriceForm;
  hourlyRate: string;
  addOns: AddOnForm[];
}

const str = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '' : String(value);

export function homeFormFrom(table: any, brandAreaUnit: AreaUnit = 'sqft'): HomePriceForm {
  const saved = table && typeof table === 'object';
  const t = saved ? table : DEFAULT_HOME_TABLE;
  // A saved table without a unit is in square feet; the template follows the brand.
  const areaUnit = saved ? asAreaUnit(t.largeHomeAreaUnit) : brandAreaUnit;
  return {
    basePrice: str(t.basePrice ?? DEFAULT_HOME_TABLE.basePrice),
    perExtraBedroom: str(t.perExtraBedroom ?? DEFAULT_HOME_TABLE.perExtraBedroom),
    perExtraBathroom: str(t.perExtraBathroom ?? DEFAULT_HOME_TABLE.perExtraBathroom),
    overrides: (Array.isArray(t.overrides) ? t.overrides : []).map((o: any) => ({
      bedrooms: str(o?.bedrooms),
      bathrooms: str(o?.bathrooms),
      price: str(o?.price),
    })),
    largeHomeSurcharge: str(t.largeHomeSurcharge),
    largeHomeSqft: str(
      saved && t.largeHomeSqft != null
        ? t.largeHomeSqft
        : SERVICE_AREA_LIMITS[areaUnit].defaultLarge,
    ),
    largeHomeAreaUnit: areaUnit,
    sameDayTurnoverSurcharge: str(t.sameDayTurnoverSurcharge),
  };
}

export function vehicleFormFrom(table: any): VehiclePriceForm {
  const t = table && typeof table === 'object' ? table : DEFAULT_VEHICLE_TABLE;
  const form = {} as VehiclePriceForm;
  for (const vehicleClass of VEHICLE_CLASSES) {
    const row = t[vehicleClass] ?? {};
    form[vehicleClass] = {} as Record<CleaningLevelKey, string>;
    for (const level of CLEANING_LEVELS) form[vehicleClass][level] = str(row?.[level]);
  }
  return form;
}

function addOnFormsFrom(serviceType: ServiceType, addOns?: Offering['addOns'] | null): AddOnForm[] {
  if (addOns && addOns.length) {
    return addOns.map(a => ({
      code: a.code,
      label: a.label,
      price: str(a.price),
      unit: a.unit,
      minutes: a.minutes ?? null,
    }));
  }
  return DEFAULT_ADD_ONS[serviceType].map(a => ({ ...a, price: str(a.price) }));
}

/** The form of one service: the saved offering, or the platform defaults when there is none. */
export function offeringToForm(
  serviceType: ServiceType,
  offering?: Offering | null,
  areaUnit: AreaUnit = 'sqft',
): OfferingForm {
  const isHome = serviceType === ServiceType.HomeTurnover;
  const isVehicle = serviceType === ServiceType.VehicleCleaning;
  return {
    serviceType,
    enabled: Boolean(offering?.isActive),
    offeringId: offering?.id ?? null,
    home: homeFormFrom(isHome ? offering?.priceTable : null, areaUnit),
    vehicle: vehicleFormFrom(isVehicle ? offering?.priceTable : null),
    hourlyRate: str(offering?.hourlyRate ?? DEFAULT_HANDOVER_HOURLY_RATE),
    addOns: addOnFormsFrom(serviceType, offering?.addOns),
  };
}

export function offeringForms(
  offerings: readonly Offering[],
  areaUnit: AreaUnit = 'sqft',
): Record<ServiceType, OfferingForm> {
  const byType = new Map(offerings.map(o => [o.serviceType, o]));
  return {
    [ServiceType.HomeTurnover]: offeringToForm(
      ServiceType.HomeTurnover,
      byType.get(ServiceType.HomeTurnover),
      areaUnit,
    ),
    [ServiceType.VehicleCleaning]: offeringToForm(
      ServiceType.VehicleCleaning,
      byType.get(ServiceType.VehicleCleaning),
    ),
    [ServiceType.VehicleHandover]: offeringToForm(
      ServiceType.VehicleHandover,
      byType.get(ServiceType.VehicleHandover),
    ),
  };
}

/** Puts a service back to the platform defaults, keeping whether it is offered. */
export function resetToDefaults(form: OfferingForm): OfferingForm {
  return {
    ...offeringToForm(form.serviceType, null, form.home.largeHomeAreaUnit),
    enabled: form.enabled,
    offeringId: form.offeringId,
  };
}

// ─── validation and the API input ───────────────────────────────────────────

export type PriceErrors = Record<string, string>;

const MONEY_MESSAGE = `Enter an amount from 0 to ${MAX_PRICE}`;

function money(raw: string, field: string, errors: PriceErrors, required: boolean): number | null {
  const text = raw.trim();
  if (!text) {
    if (required) errors[field] = 'Enter a price';
    return null;
  }
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0 || value > MAX_PRICE) {
    errors[field] = MONEY_MESSAGE;
    return null;
  }
  return Math.round(value * 100) / 100;
}

export function homeTableFromForm(form: HomePriceForm, errors: PriceErrors) {
  const overrides: { bedrooms: number; bathrooms: number; price: number }[] = [];
  const seen = new Set<string>();
  form.overrides.forEach((o, i) => {
    const field = `home.overrides.${i}`;
    const bedrooms = Number(o.bedrooms);
    const bathrooms = Number(o.bathrooms);
    const price = money(o.price, `${field}.price`, errors, true);
    if (!Number.isInteger(bedrooms) || bedrooms < 0 || bedrooms > 10 || o.bedrooms.trim() === '') {
      errors[`${field}.bedrooms`] = 'Bedrooms: 0 (studio) to 10';
      return;
    }
    if (!(bathrooms >= 0.5 && bathrooms <= 8 && Number.isInteger(bathrooms * 2))) {
      errors[`${field}.bathrooms`] = 'Bathrooms: 0.5 to 8, in halves';
      return;
    }
    const key = `${bedrooms}:${bathrooms}`;
    if (seen.has(key)) {
      errors[`${field}.bedrooms`] = 'This home size is listed twice';
      return;
    }
    seen.add(key);
    if (price !== null) overrides.push({ bedrooms, bathrooms, price });
  });
  // Spec 028 D6: the threshold's limits in its own unit.
  const areaUnit = form.largeHomeAreaUnit ?? 'sqft';
  const area = SERVICE_AREA_LIMITS[areaUnit];
  let largeHomeSqft: number | null = null;
  if (form.largeHomeSqft.trim()) {
    const sqft = Number(form.largeHomeSqft);
    if (!Number.isInteger(sqft) || sqft < area.minLarge || sqft > area.maxLarge) {
      errors['home.largeHomeSqft'] =
        `From ${area.minLarge.toLocaleString('en-US')} to ${area.maxLarge.toLocaleString('en-US')} ${areaWords(areaUnit)}`;
    } else {
      largeHomeSqft = sqft;
    }
  }
  return {
    basePrice: money(form.basePrice, 'home.basePrice', errors, true),
    perExtraBedroom: money(form.perExtraBedroom, 'home.perExtraBedroom', errors, true),
    perExtraBathroom: money(form.perExtraBathroom, 'home.perExtraBathroom', errors, true),
    overrides,
    largeHomeSurcharge: money(form.largeHomeSurcharge, 'home.largeHomeSurcharge', errors, false),
    largeHomeSqft: largeHomeSqft ?? area.defaultLarge,
    // Missing = sq ft, so a US table keeps its shape.
    ...(areaUnit === 'sqm' ? { largeHomeAreaUnit: 'sqm' } : {}),
    sameDayTurnoverSurcharge: money(
      form.sameDayTurnoverSurcharge,
      'home.sameDayTurnoverSurcharge',
      errors,
      false,
    ),
  };
}

export function vehicleTableFromForm(form: VehiclePriceForm, errors: PriceErrors) {
  const table = {} as Record<VehicleClassKey, Record<CleaningLevelKey, number | null>>;
  let priced = 0;
  for (const vehicleClass of VEHICLE_CLASSES) {
    table[vehicleClass] = {} as Record<CleaningLevelKey, number | null>;
    for (const level of CLEANING_LEVELS) {
      const value = money(
        form[vehicleClass][level],
        `vehicle.${vehicleClass}.${level}`,
        errors,
        false,
      );
      table[vehicleClass][level] = value;
      if (value !== null) priced += 1;
    }
  }
  if (priced === 0) errors['vehicle'] = 'Price at least one vehicle size and level';
  return table;
}

function addOnsFromForm(addOns: readonly AddOnForm[], errors: PriceErrors) {
  return addOns.map((a, i) => ({
    code: a.code,
    label: a.label,
    unit: a.unit,
    minutes: a.minutes,
    price: money(a.price, `addOns.${i}`, errors, true) ?? 0,
  }));
}

/** True when the table is exactly the platform template, so the API can fill it in. */
export function isDefaultTable(form: OfferingForm): boolean {
  const fresh = offeringToForm(form.serviceType, null, form.home.largeHomeAreaUnit);
  if (form.serviceType === ServiceType.HomeTurnover) {
    return JSON.stringify(form.home) === JSON.stringify(fresh.home);
  }
  if (form.serviceType === ServiceType.VehicleCleaning) {
    return JSON.stringify(form.vehicle) === JSON.stringify(fresh.vehicle);
  }
  return form.hourlyRate.trim() === fresh.hourlyRate;
}

export interface OfferingInput {
  serviceType: ServiceType;
  priceTable?: unknown;
  hourlyRate?: number;
  addOns?: { code: string; label: string; price: number; unit: string; minutes: number | null }[];
  isActive: boolean;
}

/**
 * The `upsertServiceOffering` input of one service, or the problems to show
 * next to the boxes. A new service left at the template sends no table, so
 * the API fills in the platform defaults.
 */
export function toOfferingInput(form: OfferingForm): { input: OfferingInput; errors: PriceErrors } {
  const errors: PriceErrors = {};
  const input: OfferingInput = { serviceType: form.serviceType, isActive: form.enabled };
  const useTemplate = !form.offeringId && isDefaultTable(form);
  if (form.serviceType === ServiceType.VehicleHandover) {
    const rate = Number(form.hourlyRate);
    if (!form.hourlyRate.trim() || !Number.isFinite(rate) || rate < 1 || rate > MAX_HOURLY_RATE) {
      errors.hourlyRate = `Enter an hourly rate from 1 to ${MAX_HOURLY_RATE}`;
    } else {
      input.hourlyRate = Math.round(rate * 100) / 100;
    }
  } else if (!useTemplate) {
    input.priceTable =
      form.serviceType === ServiceType.HomeTurnover
        ? homeTableFromForm(form.home, errors)
        : vehicleTableFromForm(form.vehicle, errors);
  }
  if (form.addOns.length) input.addOns = addOnsFromForm(form.addOns, errors);
  return { input, errors };
}

// ─── price per hour and the minimum wage (D26) ──────────────────────────────

/** The warning field of a price on the form (`home_turnover.basePrice`, `vehicle_cleaning.car.quick`). */
export function warningField(serviceType: ServiceType, key: string): string {
  return `${SERVICE_TYPE_KEY[serviceType]}.${key}`;
}

/** Estimated minutes of work behind a price, with the platform's default estimates. */
export function estimatedMinutes(
  serviceType: ServiceType,
  key: string,
  addOns: readonly AddOnForm[] = [],
) {
  const { home, vehicle, vehicleFactor } = DEFAULT_DURATIONS;
  if (key.startsWith('addOns.')) {
    const addOn = addOns.find(a => a.code === key.slice('addOns.'.length));
    return addOn?.minutes ?? 0;
  }
  if (serviceType === ServiceType.HomeTurnover) {
    if (key === 'basePrice') return home.baseMinutes + home.perBedroom + home.perBathroom;
    if (key === 'perExtraBedroom') return home.perBedroom;
    if (key === 'perExtraBathroom') return home.perBathroom;
    const match = /^overrides\.(\d+)bd([\d.]+)ba$/.exec(key);
    if (match) {
      return (
        home.baseMinutes +
        home.perBedroom * Math.max(1, Number(match[1])) +
        home.perBathroom * Math.max(1, Number(match[2]))
      );
    }
    return 0;
  }
  if (serviceType === ServiceType.VehicleCleaning) {
    const [vehicleClass, level] = key.split('.') as [VehicleClassKey, CleaningLevelKey];
    if (!vehicle[level] || !vehicleFactor[vehicleClass]) return 0;
    return vehicle[level] * vehicleFactor[vehicleClass];
  }
  return key === 'hourlyRate' ? 60 : 0;
}

/** Price per hour of estimated work, or null when the price or estimate is missing. */
export function effectiveHourly(price: number | null, minutes: number): number | null {
  if (price === null || !Number.isFinite(price) || !(minutes > 0)) return null;
  return Math.round(((price * 60) / minutes) * 100) / 100;
}

export const formatMoney = (value: number) => `$${value.toFixed(Number.isInteger(value) ? 0 : 2)}`;

export interface WageNote {
  hourly: number;
  below: boolean;
  text: string;
}

/**
 * The line next to a price: its price per hour, and a warning when that is
 * below the local minimum wage. A warning only: saving and publishing go on.
 */
export function wageNote(
  priceText: string,
  minutes: number,
  minimumWage: number | null | undefined,
): WageNote | null {
  const price = priceText.trim() === '' ? null : Number(priceText);
  const hourly = effectiveHourly(price, minutes);
  if (hourly === null) return null;
  const below = typeof minimumWage === 'number' && hourly < minimumWage;
  return {
    hourly,
    below,
    text: below
      ? `About ${formatMoney(hourly)} an hour, below the local minimum wage of ${formatMoney(minimumWage!)}.`
      : `About ${formatMoney(hourly)} an hour.`,
  };
}

/** The API's warnings keyed by field, for prices the form cannot estimate on its own. */
export function warningsByField(warnings: readonly PriceWarning[]): Map<string, PriceWarning> {
  return new Map(warnings.map(w => [w.field, w]));
}

export function warningText(w: PriceWarning): string {
  return `About ${formatMoney(w.effectiveHourly)} an hour, below the local minimum wage of ${formatMoney(w.minimumWage)} (${w.wageSource}).`;
}

/** Every price key a service has on the form, for the summary at the top of the page. */
export function priceKeys(form: OfferingForm): { key: string; price: string }[] {
  const keys: { key: string; price: string }[] = [];
  if (form.serviceType === ServiceType.HomeTurnover) {
    keys.push({ key: 'basePrice', price: form.home.basePrice });
    keys.push({ key: 'perExtraBedroom', price: form.home.perExtraBedroom });
    keys.push({ key: 'perExtraBathroom', price: form.home.perExtraBathroom });
    form.home.overrides.forEach(o =>
      keys.push({ key: `overrides.${o.bedrooms}bd${o.bathrooms}ba`, price: o.price }),
    );
  } else if (form.serviceType === ServiceType.VehicleCleaning) {
    for (const vehicleClass of VEHICLE_CLASSES) {
      for (const level of CLEANING_LEVELS) {
        keys.push({ key: `${vehicleClass}.${level}`, price: form.vehicle[vehicleClass][level] });
      }
    }
  } else {
    keys.push({ key: 'hourlyRate', price: form.hourlyRate });
  }
  form.addOns.forEach(a => keys.push({ key: `addOns.${a.code}`, price: a.price }));
  return keys;
}

/** How many offered prices work out below the minimum wage. */
export function belowWageCount(
  forms: readonly OfferingForm[],
  minimumWage: number | null | undefined,
): number {
  if (typeof minimumWage !== 'number') return 0;
  return forms
    .filter(f => f.enabled)
    .reduce(
      (count, form) =>
        count +
        priceKeys(form).filter(({ key, price }) => {
          const note = wageNote(
            price,
            estimatedMinutes(form.serviceType, key, form.addOns),
            minimumWage,
          );
          return note?.below;
        }).length,
      0,
    );
}
